import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useActiveTimer } from '@/hooks/useActiveTimer';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useFeatureCheck } from '@/hooks/useHasFeature';
import { useQueryClient } from '@tanstack/react-query';

import {
  mockApi,
  TYPE_LABEL_KEY,
  type MockView,
  type MockAttempt,
  type MockProgress,
  type SkillProfile,
} from '@/api/ieltsMock';
import { PageHeader } from '@/components/ui/PageHeader';
import { MockListeningAudio } from '@/components/ielts/MockListeningAudio';
import { useMockAudioQueue } from '@/hooks/useMockAudioQueue';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Mascot } from '@/components/ui/Mascot';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { MOCK_LANGUAGE } from '@/lib/mockEnglish';
import { MockEnglish } from '@/components/ielts/MockEnglish';
import { aiTrainersShown, entryHidden } from '@/lib/aiEntries';
import { useAiSkills } from '@/hooks/useAiSkills';
import { aiSkillsFailed, ensureAiSkills, type AiSkill } from '@/lib/aiSkills';
import { mockGate } from '@/lib/mockGate';
import { useAuthStore } from '@/stores/auth';
import { LiveSpeakingSession } from '@/components/speaking/LiveSpeakingSession';
import { SelectionToolbar } from '@/components/ielts/SelectionToolbar';
import type { SpeakingSession } from '@/api/ielts';
import type { SpeakingPrompt } from '@/api/ieltsMock';
import { SourceSplit } from '@/components/ielts/SourceSplit';
import { MockReviewPanel } from '@/components/ielts/MockReviewPanel';
import { TaskCard } from '@/components/ielts/MockTaskCard';
import { notifyWarning, tapHeavy } from '@/lib/haptics';
import { useBackHandler } from '@/lib/backButton';
import { Modal } from '@/components/ui/Modal';
import { useConfirm } from '@/components/ui/ConfirmProvider';

/**
 * Полный порядок разделов экзамена.
 *
 * 🔴 ЭТО ЗАПАСНОЙ ВАРИАНТ, А НЕ ИСТОЧНИК ИСТИНЫ. Состав КОНКРЕТНОЙ попытки
 * приходит с сервера (`content.sections`) и зависит от тарифа: у «только чтение
 * и слушание» разделов два, а не четыре. Пока вкладки строились отсюда, ученик
 * открывал «Письмо», которого в его экзамене нет, и страница падала на пустом
 * содержимом — поймано заказчиком 05.09.2026 прямо посреди экзамена.
 */
const SECTIONS = ['listening', 'reading', 'writing', 'speaking'];

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Friendly loading state with the mascot (Bil thinking). */
export function MascotLoading({ text }: { text: string }) {
  return (
    <Card variant="subtle" className="flex flex-col items-center gap-3 py-10 text-center">
      <Mascot pose="thinking" className="mascot h-32 w-auto object-contain" />
      <p className="text-sm text-on-surface-variant">{text}</p>
    </Card>
  );
}

/* ─────────────────────────  results view  ───────────────────────── */

function ResultsView({ res }: { res: MockAttempt }) {
  const { t } = useTranslation();
  // Проверка тарифа: спринт есть не у всех, и звать туда всех подряд нельзя.
  const can = useFeatureCheck();
  // Спринт закрывает и его тумблер (docs/CONTRACTS/ai-skills.md) — entryHidden ниже.
  useAiSkills();
  const bands = res.per_skill_bands ?? {};
  const strong = res.strong_types ?? [];
  const weak = res.weak_types ?? [];
  const perType = res.per_type_accuracy ?? {};
  const [detail, setDetail] = useState(false);
  const band = res.band_overall ?? 0;
  const strongResult = band >= 6;

  return (
    <div className="space-y-6">
      {/* Mascot reaction — celebration on a strong result, encouragement otherwise */}
      <Card className="flex flex-col items-center gap-3 py-8 text-center">
        <Mascot
          pose={strongResult ? 'celebrate' : 'encourage'}
          alt="Bil"
          className={`${strongResult ? 'mascot-celebrate' : 'mascot'} h-48 w-auto object-contain`}
        />
        <h2 className="font-display text-2xl font-bold tracking-display text-on-surface">
          {t(strongResult ? 'ieltsMock.resultStrong' : 'ieltsMock.resultStart')}
        </h2>
        <p className="text-on-surface-variant">
          {t(strongResult ? 'ieltsMock.resultStrongSub' : 'ieltsMock.resultStartSub')}
        </p>
        {/* Балл — ПРИМЕРНЫЙ (владелец 20.10.2026): считается по верным ответам, а не экзаменатором.
            Расчёт не менялся, менялась подпись — на всех экранах, где виден балл мока. */}
        <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
          <span className="text-sm font-semibold text-on-surface-variant">{t('ieltsMock.approxBandLabel')}</span>
          {/* Балл — с десятой, как пишет IELTS и как в профиле («6.0», а не «6»). */}
          <span className="text-6xl font-bold text-primary">{res.band_overall != null ? res.band_overall.toFixed(1) : '—'}</span>
        </div>
        <p className="max-w-sm text-xs text-on-surface-variant">{t('ieltsMock.approxBandNote')}</p>
        <Button variant="secondary" onClick={() => setDetail((d) => !d)}>
          {t(detail ? 'ieltsMock.detailHide' : 'ieltsMock.detailShow')}
        </Button>
      </Card>

      {detail && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {/* Только разделы с баллом: «Approximate IELTS band: —» у раздела, которого в моке не было, — шум. */}
            {SECTIONS.filter((s) => bands[s] != null).map((s) => (
              <Card key={s} variant="subtle" className="text-center">
                <div className="text-xs text-on-surface-variant">{t(`trainersHub.skill.${s}`)}</div>
                <div className="text-[11px] text-on-surface-variant">{t('ieltsMock.approxBandLabel')}</div>
                <div className="text-2xl font-semibold">{bands[s]!.toFixed(1)}</div>
              </Card>
            ))}
          </div>
          <p className="text-center text-xs text-on-surface-variant">{t('ieltsMock.approxBandNote')}</p>

          <div className="grid gap-4 md:grid-cols-2">
            <Card variant="subtle" className="space-y-2">
              <CardTitle>{t('ieltsMock.strongTypes')}</CardTitle>
              {strong.length === 0 && <p className="text-sm text-on-surface-variant">{t('ieltsMock.strongEmpty')}</p>}
              <div className="flex flex-wrap gap-2">
                {strong.map((tp) => (
                  <Pill key={tp} variant="success">
                    {t(TYPE_LABEL_KEY[tp] ?? tp)}
                  </Pill>
                ))}
              </div>
            </Card>
            <Card variant="subtle" className="space-y-2">
              <CardTitle>{t('ieltsMock.weakTypes')}</CardTitle>
              {weak.length === 0 && <p className="text-sm text-on-surface-variant">{t('ieltsMock.weakEmpty')}</p>}
              <div className="flex flex-wrap gap-2">
                {weak.map((tp) => (
                  <Pill key={tp} variant="warning">
                    {t(TYPE_LABEL_KEY[tp] ?? tp)}
                  </Pill>
                ))}
              </div>
            </Card>
          </div>

          {Object.keys(perType).length > 0 && (
            <Card variant="subtle" className="space-y-2">
              <CardTitle>{t('ieltsMock.perTypeTitle')}</CardTitle>
              <div className="space-y-1.5">
                {Object.entries(perType).map(([type, tt]) => (
                  <div key={type} className="flex items-center justify-between gap-3 text-sm">
                    <span>{t(TYPE_LABEL_KEY[type] ?? type)}</span>
                    <span className="tabular-nums text-on-surface-variant">
                      {tt.correct}/{tt.total} · {Math.round(tt.acc * 100)}%
                    </span>
                  </div>
                ))}
              </div>
              <p className="pt-1 text-xs text-on-surface-variant">
                {t('ieltsMock.perTypeNote')}
              </p>
            </Card>
          )}
        </>
      )}

      {/* Разбор по каждому вопросу (20.10.2026): ответ, ключ, объяснение, опора. */}
      <MockReviewPanel />

      <Card className="flex flex-col items-center gap-3 text-center">
        <p className="text-sm text-on-surface-variant">{t('ieltsMock.unlocked')}</p>
        <div className="flex flex-wrap justify-center gap-3">
          {/* ⚠️ СПРИНТ ПОКАЗЫВАЕМ ТОЛЬКО ТЕМ, У КОГО ОН В ТАРИФЕ. Кнопка стояла
              без проверки, и ученик тарифа «чтение и слушание» попадал на
              страницу, которая падала с английской ошибкой (07.09.2026).
              Приглашение туда, куда человеку нельзя, — обещание, которое
              платформа не выполнит. */}
          {can('sprint') && !entryHidden('/app/ielts/sprint') && (
            <Link to="/app/ielts/sprint">
              <Button size="lg">{t('ieltsMock.sprintCta')}</Button>
            </Link>
          )}
          <Link to="/app/trainers">
            <Button variant="secondary" size="lg">{t('ieltsMock.allTrainers')}</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────  speaking (live)  ───────────────────────── */

export function MockSpeaking({
  prompts,
  done,
  onDone,
}: {
  prompts: SpeakingPrompt[];
  done: boolean;
  onDone: (transcript: string) => void;
}) {
  const { t } = useTranslation();
  const [band, setBand] = useState<number | undefined>();
  const [started, setStarted] = useState(false);

  if (prompts.length === 0) return <Card variant="subtle">{t('ieltsMock.speakingEmpty')}</Card>;

  return (
    <div className="space-y-6">
      <Card variant="subtle" className="space-y-2">
        <div className="flex items-center gap-3">
          {/* В store-сборке маскота нет (D76, components/ui/Mascot.tsx) — и файла mascot/ в ней нет. */}
          {!IS_APP_BUILD && <img src="/mascot/bil_speaking.webp?v=3" alt="" className="mascot h-14 w-auto object-contain" />}
          <CardTitle>{t('ieltsMock.speakingTitle')}</CardTitle>
        </div>
        <p className="text-sm text-on-surface-variant">
          {t('ieltsMock.speakingIntro')}
        </p>
        {prompts.map((sp) => (
          <div key={sp.id} className="text-sm">
            <span className="font-semibold text-primary">Part {sp.part}.</span> {sp.topic}
          </div>
        ))}
      </Card>

      {done ? (
        <Card className="space-y-1 text-center">
          <Pill variant="success">{t('ieltsMock.speakingDone')}</Pill>
          {band != null && <div className="text-2xl font-semibold">{t('ieltsMock.speakingBand', { band })}</div>}
          <p className="text-sm text-on-surface-variant">{t('ieltsMock.speakingCounted')}</p>
        </Card>
      ) : !started ? (
        <Card className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-on-surface-variant">
            {t('ieltsMock.speakingStartHint')}
          </p>
          <Button size="lg" onClick={() => setStarted(true)}>
            {t('ieltsMock.speakingStart')}
          </Button>
        </Card>
      ) : (
        <LiveSpeakingSession
          cueCardId={null}
          onGraded={(session: SpeakingSession) => {
            const studentText = (session.turns ?? [])
              .filter((tn) => tn.role === 'student')
              .map((tn) => tn.content)
              .join(' ')
              .trim();
            setBand(session.band_overall);
            onDone(studentText || t('ieltsMock.speakingHeld'));
          }}
          onExit={() => setStarted(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────  main page  ───────────────────────── */

/**
 * 🔴 Mock test целиком по-английски при любом языке интерфейса (решение владельца
 * 20.10.2026) — это не потерянный перевод. Почему обёрткой экрана, а не ключами, —
 * `lib/mockEnglish.ts`; сторож — `lib/mockEnglish.test.ts`.
 */
export function StudentIELTSMockPage() {
  return (
    <MockEnglish>
      <MockScreen />
    </MockEnglish>
  );
}

function MockScreen() {
  const { t } = useTranslation();
  // Какие навыки открыты тарифом — по ним же собирается сам мок на сервере.
  const can = useFeatureCheck();
  // И тумблерами навыков (12.10.2026, docs/CONTRACTS/ai-skills.md): сервер
  // собирает мок только из `mock_skills`.
  const aiSkills = useAiSkills();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const ask = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [view, setView] = useState<MockView | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // Сколько времени дано на эту попытку. Хук отсчитывает от него и сам
  // останавливается, когда вкладку скрыли.
  const [startSeconds, setStartSeconds] = useState(0);
  const [sectionIdx, setSectionIdx] = useState(0);
  // Записи Listening — один раз, части подряд (MockListeningAudio, 21.10.2026); с 27.10.2026 (сборка 1.1.2) и на
  // телефоне. Прослушанное — в попытке (техдолг 432): пауза, конец части и ход записи сохраняют попытку.
  const saveRef = useRef<() => void>(() => undefined);
  const audioQueue = useMockAudioQueue(() => saveRef.current());
  const { snapshot: snapshotAudio, restore: restoreAudio } = audioQueue;
  const [phase, setPhase] = useState<'loading' | 'intro' | 'exam' | 'submitting' | 'results' | 'error'>('loading');

  // Аппаратная «Назад» во время экзамена не уводит молча: идёт отсчёт, и уход
  // означает потерянную попытку. Перехватчик стоит выше навигации — пока висит
  // вопрос, кнопка занята им (см. lib/backButton).
  const [leaveAsk, setLeaveAsk] = useState(false);
  const askLeave = useCallback(() => {
    setLeaveAsk(true);
    return true;
  }, []);
  useBackHandler(askLeave, phase === 'exam');
  const [result, setResult] = useState<MockAttempt | null>(null);
  const [profile, setProfile] = useState<SkillProfile | null>(null);
  const [err, setErr] = useState('');

  const readingRef = useRef<HTMLDivElement>(null);
  const listeningRef = useRef<HTMLDivElement>(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  // ⚠️ Объявлен ДО таймера намеренно пустым: сохранение прогресса читает
  // остаток из ссылки, а сам таймер объявляется ниже (ему нужен doSubmit).
  const remainingRef = useRef(0);
  const sectionRef = useRef(sectionIdx);
  sectionRef.current = sectionIdx;
  // ⚠️ СОХРАНЯЕМ РАЗДЕЛ ИЗ СОСТАВА ПОПЫТКИ, а не из общего списка. Именно так у
  // ученика с двумя разделами оказалось записано «письмо»: сохранение брало
  // элемент зашитого списка, которого в его экзамене нет. После этого страница
  // падала при каждом открытии.
  const planRef = useRef<string[]>(SECTIONS);
  planRef.current = view?.content?.sections?.length ? view.content.sections : SECTIONS;

  const attemptId = view?.attempt.id;

  const save = useCallback(
    (extra?: Partial<MockProgress>) => {
      if (!attemptId) return;
      mockApi
        .saveProgress(attemptId, {
          answers: answersRef.current,
          remaining_seconds: remainingRef.current,
          current_section: planRef.current[sectionRef.current] ?? planRef.current[0],
          current_index: 0,
          // Прослушанное каждой части — и на компьютере, и на телефоне (с 27.10.2026, сборка 1.1.2).
          section_states: snapshotAudio(),
          ...extra,
        })
        .catch((e) => {
          /**
           * ⚠️ НЕ МОЛЧИМ, ХОТЯ И НЕ ТРЕВОЖИМ. Сбой сохранения глотался наглухо:
           * ответы не доезжали, экзамен шёл дальше, и понять это было нельзя ни
           * ученику, ни нам. Показывать ошибку посреди экзамена — пугать
           * человека на ровном месте: следующая попытка будет через полторы
           * секунды и скорее всего пройдёт. А след в журнале превращает разбор
           * жалобы «у меня всё пропало» в вопрос одной минуты.
           */
          console.warn('[экзамен] ответы не сохранились, повторим:', e);
        });
    },
    [attemptId, snapshotAudio],
  );
  saveRef.current = () => save();

  /**
   * Сдача работы.
   *
   * ⚠️ `byTimeout` ОТЛИЧАЕТ ДВА РАЗНЫХ СОБЫТИЯ, И ПУТАТЬ ИХ НЕЛЬЗЯ. Человек
   * нажал «Завершить» — это его решение, и мы спрашиваем подтверждение. Время
   * вышло — это не решение, спрашивать некого и нечего: работа уходит как есть.
   * Заказчик 07.09.2026: «если чел реально не успел — то ок, автозакрытие, и
   * если не успел, то действительно считается, что не сдал».
   */
  const doSubmit = useCallback(async (byTimeout = false) => {
    if (!attemptId) return;
    void byTimeout;
    setPhase('submitting');
    try {
      const res = await mockApi.submit(attemptId, answersRef.current);
      setResult(res);
      setPhase('results');
      qc.invalidateQueries({ queryKey: ['ielts', 'profile'] });
      qc.invalidateQueries({ queryKey: ['ielts-mock-review'] }); // разбор — уже этой попытки
    } catch (e) {
      setErr(humanizeError(e, MOCK_LANGUAGE));
      setPhase('error');
    }
  }, [attemptId, qc]);

  // intro: show the student's status without creating an attempt
  useEffect(() => {
    (async () => {
      try {
        const p = await mockApi.profile();
        setProfile(p);
      } catch {
        /* no profile yet — fine */
      }
      setPhase('intro');
    })();
  }, []);

  // begin / resume the actual exam (creates or resumes an attempt)
  const begin = useCallback(async () => {
    setPhase('loading');
    try {
      const v = await mockApi.start('B2');
      if (v.attempt.status === 'completed') {
        setResult(v.attempt);
        setPhase('results');
        return;
      }
      // До setView: первое же сохранение попытки должно нести восстановленное, а не пустое.
      restoreAudio(v.attempt.section_states);
      setView(v);
      setAnswers((v.attempt.answers as Record<string, string>) ?? {});
      setStartSeconds(v.attempt.remaining_seconds || 0);
      const si = v.content.sections?.indexOf(v.attempt.current_section) ?? 0;
      setSectionIdx(si >= 0 ? si : 0);
      setPhase('exam');
    } catch (e) {
      // Мок — событие с датой (13.10.2026): без окна — 423 mock_not_scheduled,
      // текст «Mock test ещё не назначен» даёт humanizeError по коду.
      setErr(humanizeError(e, MOCK_LANGUAGE));
      setPhase('error');
    }
  }, [restoreAudio]);

  /**
   * ⚠️ ЧАСЫ ИДУТ, ТОЛЬКО ПОКА ВКЛАДКА ОТКРЫТА. Раньше отсчёт шёл всегда:
   * человек отвлёкся на звонок, вернулся — экзамен сдался сам, и в результате
   * нули. Наказание за то, что зазвонил телефон.
   *
   * Заказчик 07.09.2026: «таймер должен считать только тогда, когда ученик
   * находится во вкладке спринта или мока».
   *
   * ⚠️ Предупреждение за минуту до конца выдаётся РОВНО ОДИН раз: без флага оно
   * повторялось бы каждую секунду последней минуты.
   */
  const warned = useRef(false);
  useEffect(() => {
    if (phase !== 'exam') warned.current = false;
  }, [phase]);

  const remaining = useActiveTimer(startSeconds, phase === 'exam', () => {
    notifyWarning(); // время вышло, работа уходит сама
    void doSubmit(true);
  });

  remainingRef.current = remaining;

  useEffect(() => {
    if (phase === 'exam' && remaining <= 60 && remaining > 0 && !warned.current) {
      warned.current = true;
      notifyWarning();
    }
  }, [phase, remaining]);

  /**
   * ⚠️ СОХРАНЯЕМ ПОСЛЕ КАЖДОГО ОТВЕТА, А НЕ РАЗ В ПЯТНАДЦАТЬ СЕКУНД.
   *
   * Было так: раз в 15 секунд и ещё раз при уходе со страницы. Ученик отвечал
   * на несколько вопросов и выходил — часть ответов сохранялась, часть
   * пропадала. Ровно это заказчик и увидел 07.09.2026: «проставил ответы,
   * вышел — обнулились. А стоп, некоторые сохранились, некоторые нет».
   *
   * Почему уход со страницы не спасал: обычный запрос браузер обрывает, когда
   * страница закрывается, а ошибку мы глотали. Экзамен — не то место, где можно
   * терять работу человека.
   *
   * Ждём полторы секунды после последнего нажатия: печатая длинный ответ, ученик
   * иначе слал бы запрос на каждую букву.
   */
  useEffect(() => {
    if (phase !== 'exam') return;
    const t = setTimeout(() => save(), 1500);
    return () => clearTimeout(t);
  }, [phase, save, answers]);

  // Раз в 15 секунд — подстраховка на случай, когда ученик долго читает и
  // ничего не нажимает: время экзамена всё равно должно записаться.
  useEffect(() => {
    if (phase !== 'exam') return;
    const t = setInterval(() => save(), 15000);
    return () => {
      clearInterval(t);
      save();
    };
  }, [phase, save]);

  /**
   * ⚠️ МАЯКА `sendBeacon` ЗДЕСЬ НЕТ, ХОТЯ ПРОСИТСЯ. Он умеет доставлять запрос
   * даже после закрытия вкладки — но не умеет нести заголовок входа, а делать
   * ради него ручку без проверки прав значит открыть дыру: чужой смог бы писать
   * ответы в чужую попытку.
   *
   * Полутора секунд после ответа хватает: чтобы потерять работу, надо успеть
   * закрыть вкладку в эти полторы секунды сразу после нажатия.
   */

  const onAnswer = useCallback((qid: string, val: string) => {
    setAnswers((a) => ({ ...a, [qid]: val }));
  }, []);

  const content = view?.content;
  const answeredCount = useMemo(() => Object.values(answers).filter((v) => v !== '').length, [answers]);

  /**
   * Сколько вопросов в работе всего — чтобы понять, всё ли отвечено.
   *
   * ⚠️ СЧИТАЕМ ПО ФАКТИЧЕСКОМУ СОСТАВУ, А НЕ ПО ЧИСЛУ ИЗ МАКЕТА. Состав мока
   * собирается на сервере и зависит от тарифа: у кого-то только чтение и
   * слушание. Зашитая «сорок вопросов» обещала бы работу, которой нет.
   */
  const totalQuestions = useMemo(() => {
    if (!content) return 0;
    const packs = [content.listening ?? [], content.reading ?? []];
    let n = 0;
    for (const pack of packs) {
      for (const src of pack) {
        for (const task of src.tasks ?? []) n += (task.questions ?? []).length;
      }
    }
    // Письмо и говорение — по одному заданию каждое, вопросов в них нет.
    n += (content.writing ?? []).length + (content.speaking ?? []).length;
    return n;
  }, [content]);

  const allAnswered = totalQuestions > 0 && answeredCount >= totalQuestions;

  if (phase === 'intro') {
    const done = profile?.mock_done;
    // Мок — событие с датой (13.10.2026, docs/CONTRACTS/ai-skills.md). «Сдан» по
    // профилю ещё не значит «смотреть результаты»: в открытом окне сервер соберёт
    // новый мок — пересдачу или мини-диагностику по навыку, включённому позже.
    // ⚠️ Без этого кнопка «Посмотреть результаты» запускала мини-диагностику.
    const mockState = aiSkills.mock_state;
    // Сбой запроса навыков — не «не назначен» (lib/mockGate.ts, 20.10.2026).
    const gate = mockGate(mockState, aiSkillsFailed());
    const mini = !!done && mockState !== 'passed' && aiSkills.mock_skills.some((k) => !aiSkills[k as AiSkill]);
    const introText = gate.status === 'failed'
      ? t('ieltsMock.statusFailed')
      : gate.status === 'not_scheduled'
      ? t('ieltsMock.notScheduled')
      : mini ? t('ieltsMock.miniText')
        : done ? (mockState === 'open' ? t('ieltsMock.retakeOpenText') : t('ieltsMock.onceNote'))
          : t('ieltsMock.gateText');
    const bands = profile?.per_skill_band ?? {};
    const bandKeys = (['listening', 'reading', 'writing', 'speaking'] as const).filter((k) => bands[k] != null);
    // Части экзамена. ⚠️ БЕЗ ЧИСЕЛ ИЗ МАКЕТА («4 раздела · 40 вопросов ·
    // 30 мин»): состав нашего мока собирается на сервере и меняется, а
    // печатать чужие цифры экзамена как свои — обманывать.
    //
    // 🔴 ПОКАЗЫВАЕМ ТОЛЬКО РАЗРЕШЁННЫЕ ТАРИФОМ НАВЫКИ. Список был зашит на все
    // четыре, и ученик с тарифом «только чтение и слушание» читал здесь про
    // письмо и говорение, хотя в самом моке их не было. Сервер собирает мок
    // правильно (`assemble` в ielts_pool_mock.go) — врала именно эта заставка.
    // Замечено заказчиком 05.09.2026.
    const allPapers = [
      { key: 'listening', icon: 'headphones', tone: 'info', feature: 'ielts_listening' },
      { key: 'reading', icon: 'menu_book', tone: 'primary', feature: 'ielts_reading' },
      { key: 'writing', icon: 'edit_note', tone: 'info', feature: 'ielts_writing' },
      { key: 'speaking', icon: 'mic', tone: 'primary', feature: 'ielts_speaking' },
    ] as const;
    // С 12.10.2026 ещё и тумблеры навыков: выключенный навык в мок не входит.
    const papers = allPapers.filter((p) => can(p.feature) && aiSkills.mock_skills.includes(p.key));

    return (
      <div>
        <Link
          to="/app/trainers"
          className="mb-4 flex w-max items-center gap-[9px] text-[12.5px] font-semibold text-on-surface-variant hover:text-on-surface"
        >
          <span className="material-symbols-outlined text-[15px]">chevron_left</span>
          {t('trainersHub.title')}
        </Link>

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <div className="relative min-h-[190px] overflow-hidden rounded-[24px] bg-primary px-[26px] py-6">
              <div className="max-w-[400px]">
                <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-on-primary/85">
                  {t('ieltsMock.kicker')}
                </div>
                <h1 className="my-[10px] mb-[7px] font-display text-[28px] font-bold leading-[1.1] text-on-primary">
                  {/* Тренажёры закрыты (D26) — «чтобы открыть тренажёры» было бы неправдой. */}
                  {mini
                    ? t('ieltsMock.miniTitle')
                    : done
                      ? t('ieltsMock.retakeTitle')
                      : aiTrainersShown() ? t('ieltsMock.gateTitle') : t('ieltsMock.gateTitleTrainersClosed')}
                </h1>
                <p className="text-[13px] leading-[1.6] text-on-primary/90">{introText}</p>
                <div className="mt-[18px] flex flex-wrap items-center gap-[11px]">
                  <button
                    type="button"
                    onClick={() => void begin()}
                    disabled={gate.startDisabled}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-[22px] py-[13px] text-[14.5px] font-bold text-primary-pressed transition hover:bg-primary-container disabled:opacity-60"
                  >
                    {done && mockState === 'passed' ? t('ieltsMock.seeResults') : t('ieltsMock.start')}
                    <span className="material-symbols-outlined text-[17px]">arrow_forward</span>
                  </button>
                  {gate.retry && (
                    <button
                      type="button"
                      onClick={() => void ensureAiSkills(userId)}
                      className="inline-flex items-center rounded-full bg-on-primary/20 px-[22px] py-[13px] text-[13.5px] font-semibold text-on-primary transition hover:bg-on-primary/30"
                    >
                      {t('ieltsMock.statusRetry')}
                    </button>
                  )}
                  {/* Та же проверка тарифа: без неё кнопка ведёт в раздел,
                      недоступный ученику, и страница там падает. Спринт закрыт
                      вместе с ИИ-тренажёрами (D26, 11.09.2026). */}
                  {can('sprint') && !entryHidden('/app/ielts/sprint') && (
                    <Link
                      to="/app/ielts/sprint"
                      className="inline-flex items-center rounded-full bg-on-primary/20 px-[22px] py-[13px] text-[13.5px] font-semibold text-on-primary transition hover:bg-on-primary/30"
                    >
                      {t('ieltsMock.toSprints')}
                    </Link>
                  )}
                </div>
              </div>
              <Mascot
                pose="books"
                alt="Bil"
                className="pointer-events-none absolute bottom-0 right-5 hidden h-[186px] w-auto object-contain object-bottom sm:block"
              />
            </div>

            {/* Состав неизвестен (сбой запроса навыков) — пустой список и заголовок над ним не рисуем. */}
            {papers.length > 0 && (
            <h2 className="mx-0.5 mb-[11px] mt-[22px] font-display text-[15.5px] font-bold text-on-surface">
              {t('ieltsMock.whatIsInside')}
            </h2>
            )}
            {papers.length > 0 && (
            <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
              {papers.map((p) => (
                <div key={p.key} className="flex items-center gap-3 border-b border-outline py-3 last:border-b-0">
                  <span
                    className={cn(
                      'grid h-[34px] w-[34px] flex-none place-items-center rounded-xl',
                      p.tone === 'info' ? 'bg-info-container' : 'bg-primary-container',
                    )}
                  >
                    <span className={cn('material-symbols-outlined text-[17px]', p.tone === 'info' ? 'text-info' : 'text-primary')}>
                      {p.icon}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] font-semibold text-on-surface">
                      {t(`trainersHub.ielts${p.key[0].toUpperCase()}${p.key.slice(1)}`)}
                    </span>
                    <span className="block text-[12.5px] text-on-surface-variant">{t(`ieltsMock.paper.${p.key}`)}</span>
                  </span>
                </div>
              ))}
            </div>
            )}

            <div className="mt-3.5 flex items-center gap-3 rounded-[20px] bg-info-container px-[18px] py-[15px]">
              <span className="material-symbols-outlined flex-none text-[19px] text-info">info</span>
              {/* ⚠️ ПРО МИКРОФОН ГОВОРИМ, ТОЛЬКО ЕСЛИ ГОВОРЕНИЕ ЕСТЬ В ТАРИФЕ.
                  Иначе ученик, у которого в моке лишь чтение и слушание, читает
                  «для говорения нужен микрофон», ищет говорение и не находит —
                  и решает, что часть экзамена от него спрятали (заказчик,
                  07.09.2026). */}
              <p className="flex-1 text-[12.5px] leading-[1.55] text-on-surface-variant">
                {/* С 20.10.2026 — по составу ЭТОГО мока, а не по тарифу: у пилота (Reading + Listening)
                    говорение в тарифе есть, а в моке нет. */}
                {papers.some((p) => p.key === 'speaking') ? t('ieltsMock.rules') : t('ieltsMock.rulesNoMic')}
              </p>
            </div>
          </div>

          {/* ── Прошлый результат ──────────────────────────────────────── */}
          <div>
            {done && (
              <div className="rounded-[22px] bg-surface-container px-[22px] py-5">
                <h3 className="mb-[15px] text-[14px] font-bold text-on-surface">{t('ieltsMock.lastResult')}</h3>
                <div className="flex items-center gap-4">
                  <span className="flex h-[78px] w-[78px] flex-none flex-col items-center justify-center rounded-full bg-primary-container">
                    <span className="font-display text-[26px] font-bold leading-none text-on-primary-container">
                      {profile?.level ?? '—'}
                    </span>
                    <span className="mt-1 text-[8.5px] font-semibold uppercase tracking-[0.08em] text-on-primary-container/80">
                      {t('ieltsMock.overall')}
                    </span>
                  </span>
                  {bandKeys.length > 0 && (
                    <span className="flex min-w-0 flex-1 flex-col gap-[7px]">
                      <span className="text-[10.5px] font-semibold text-on-surface-variant">{t('ieltsMock.approxBandLabel')}</span>
                      {bandKeys.map((k) => (
                        <span key={k} className="flex items-center gap-2">
                          <span className="flex-1 text-[11.5px] font-semibold text-on-surface-variant">
                            {t(`trainersHub.ielts${k[0].toUpperCase()}${k.slice(1)}`)}
                          </span>
                          <span className="text-[11.5px] font-bold text-on-surface">{bands[k]!.toFixed(1)}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </div>
                {bandKeys.length > 0 && (
                  <p className="mt-2.5 text-[10.5px] leading-[1.5] text-on-surface-variant">{t('ieltsMock.approxBandNote')}</p>
                )}
                <button
                  type="button"
                  onClick={() => void begin()}
                  className="mt-3 rounded-full bg-info-container px-4 py-[9px] text-[11.5px] font-bold text-info transition hover:brightness-95"
                >
                  {t('ieltsMock.seeResults')}
                </button>
              </div>
            )}

            <div className={cn('rounded-[22px] bg-info-container px-5 py-[18px]', done && 'mt-3.5')}>
              <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-info">
                {done ? t('ieltsMock.whyRetake') : t('ieltsMock.whyTake')}
              </div>
              <p className="mt-2.5 text-[13px] leading-[1.6] text-on-surface-variant">{t('ieltsMock.whyText')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (phase === 'loading') return <MascotLoading text={t('ieltsMock.loading')} />;
  if (phase === 'error')
    return (
      <Card variant="subtle" className="text-error">
        {err || t('common.error')}
      </Card>
    );
  if (phase === 'results' && result)
    return (
      <>
        <PageHeader title={t('ieltsMock.resultsTitle')} subtitle={t('ieltsMock.resultsSubtitle')} />
        <ResultsView res={result} />
      </>
    );
  if (phase === 'submitting') return <MascotLoading text={t('ieltsMock.submitting')} />;
  if (!content) return null;

  // ⚠️ Разделы берём ИЗ САМОЙ ПОПЫТКИ, а не из зашитого списка. Состав зависит
  // от тарифа: у «только чтение и слушание» разделов два. Пока вкладки шли из
  // общего списка, ученик мог открыть «Письмо», которого в его экзамене нет, —
  // и страница падала на пустом содержимом.
  const sections = content.sections?.length ? content.sections : SECTIONS;
  // ⚠️ ПУСТОЙ РАЗДЕЛ — ЭТО NULL, А НЕ ПУСТОЙ СПИСОК. Сервер не кладёт письмо и
  // говорение в попытку, где их нет, и `.map` по такому полю роняет всю
  // страницу целиком: ученик видит «Unexpected Application Error» посреди
  // экзамена, а не пустой раздел. Подставляем пустые списки один раз здесь.
  const listening = content.listening ?? [];
  const reading = content.reading ?? [];
  const writing = content.writing ?? [];
  const speaking = content.speaking ?? [];
  const section = sections[sectionIdx] ?? sections[0];
  const isLast = sectionIdx === sections.length - 1;
  const lowTime = remaining < 120;

  return (
    <>
      {/* sticky exam bar */}
      <div className="sticky top-0 z-20 -mx-4 mb-4 flex items-center justify-between gap-3 border-b border-outline bg-surface/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-2 overflow-x-auto">
          {sections.map((s, i) => (
            <button
              key={s}
              onClick={() => {
                save();
                setSectionIdx(i);
              }}
              className={cn(
                'whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition',
                i === sectionIdx ? 'bg-primary text-on-primary' : 'bg-surface-container-high text-on-surface-variant hover:text-on-surface',
              )}
            >
              {t(`trainersHub.skill.${s}`)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-on-surface-variant">{t('ieltsMock.answered', { n: answeredCount })}</span>
          <span
            className={cn(
              'rounded-lg px-3 py-1 font-mono text-sm font-semibold tabular-nums',
              lowTime ? 'bg-error/15 text-error' : 'bg-surface-container-high text-on-surface',
            )}
          >
            ⏱ {fmt(remaining)}
          </span>
        </div>
      </div>

      <PageHeader title={t('ieltsMock.title')} subtitle={t('ieltsMock.subtitleShort')} />

      {/* ── Listening ── */}
      {section === 'listening' && (
        <div className="space-y-6" ref={listeningRef}>
          {/* Панель выделения на инструкциях, вопросах и вариантах (T4, 21.10.2026; телефон — с 27.10.2026). */}
          <SelectionToolbar containerRef={listeningRef} />
          {listening.map((b, i) => (
            <SourceSplit
              key={b.audio.id}
              // Плеер закреплён сверху, не прячется за вкладку. Запись звучит один раз,
              // части подряд (21.10.2026), на телефоне так же (27.10.2026).
              kind="audio"
              source={
                <Card className="space-y-2">
                  <CardTitle>{b.audio.title || t('ieltsMock.audio')}</CardTitle>
                  <MockListeningAudio src={b.audio.audio_url} index={i} count={listening.length} queue={audioQueue} exam />
                </Card>
              }
              questions={
                <div className="space-y-4">
                  {b.tasks.map((t) => (
                    <TaskCard key={t.id} task={t} answers={answers} onAnswer={onAnswer} />
                  ))}
                </div>
              }
            />
          ))}
          {listening.length === 0 && <Card variant="subtle">{t('ieltsMock.listeningEmpty')}</Card>}
        </div>
      )}

      {/* ── Reading ── */}
      {section === 'reading' && (
        <div className="space-y-8" ref={readingRef}>
          <SelectionToolbar containerRef={readingRef} />
          {reading.map((b) => (
            <SourceSplit
              key={b.text.id}
              source={
                <Card className="lg:max-h-[70vh] lg:overflow-y-auto">
                  <CardTitle>{b.text.title}</CardTitle>
                  <div className="mt-2 whitespace-pre-line text-sm leading-relaxed">{b.text.body}</div>
                </Card>
              }
              questions={
                <div className="space-y-4">
                  {b.tasks.map((t) => (
                    <TaskCard key={t.id} task={t} answers={answers} onAnswer={onAnswer} />
                  ))}
                </div>
              }
            />
          ))}
          {reading.length === 0 && <Card variant="subtle">{t('ieltsMock.readingEmpty')}</Card>}
        </div>
      )}

      {/* ── Writing ── */}
      {section === 'writing' && (
        <div className="space-y-6">
          {writing.map((w) => (
            <Card key={w.id} className="space-y-3">
              <Pill variant="primary">{w.kind === 'task2' ? 'Writing Task 2' : 'Writing Task 1'}</Pill>
              <p className="whitespace-pre-line text-sm">{w.prompt}</p>
              <textarea
                value={answers[w.id] ?? ''}
                onChange={(e) => onAnswer(w.id, e.target.value)}
                rows={10}
                placeholder={t('ieltsMock.essayPlaceholder')}
                className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
              />
              <div className="text-right text-xs text-on-surface-variant">
                {t('mock.wordCount', { count: answers[w.id]?.trim().split(/\s+/).filter(Boolean).length ?? 0 })}
              </div>
            </Card>
          ))}
          {writing.length === 0 && <Card variant="subtle">{t('ieltsMock.writingEmpty')}</Card>}
        </div>
      )}

      {/* ── Speaking — live voice conversation with the AI examiner ── */}
      {section === 'speaking' && (
        <MockSpeaking
          prompts={speaking}
          done={!!answers['__speaking_done']}
          onDone={(transcript) => {
            if (speaking[0]) onAnswer(speaking[0].id, transcript);
            onAnswer('__speaking_done', '1');
          }}
        />
      )}

      {/* footer nav */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          disabled={sectionIdx === 0}
          onClick={() => {
            save();
            setSectionIdx((i) => Math.max(0, i - 1));
          }}
        >
          {t('ieltsMock.back')}
        </Button>
        {isLast ? (
          <Button
            onClick={async () => {
              /**
               * ⚠️ НЕОТВЕЧЕННЫЕ ВОПРОСЫ СЧИТАЮТСЯ ОШИБКАМИ, ПОЭТОМУ ПРО НИХ
               * НАДО СКАЗАТЬ ВСЛУХ. Заказчик 07.09.2026: «нельзя сдать пустой
               * мок или спринт, должно быть сообщение — ты не завершил все
               * задания».
               *
               * ⚠️ НО СДАТЬ ВСЁ-ТАКИ МОЖНО. Запретить наглухо нельзя: человек
               * имеет право уйти, не зная ответа, и упереться в кнопку, которая
               * не нажимается, — хуже, чем предупреждение. Мы называем цифру и
               * спрашиваем ещё раз.
               */
              const left = totalQuestions - answeredCount;
              const message = allAnswered
                ? t('ieltsMock.finishConfirm')
                : t('ieltsMock.finishIncomplete', { left });
              // Диалог рисуется у корня, вне английской обёртки: все подписи — отсюда (lib/mockEnglish.ts).
              if (await ask({
                message,
                title: t('common.confirmTitle'),
                confirmLabel: t('ieltsMock.finishShort'),
                cancelLabel: t('common.cancel'),
              })) {
                tapHeavy(); // крупное событие — тест сдан
                void doSubmit();
              }
            }}
          >
            {t('ieltsMock.finishCta')}
          </Button>
        ) : (
          <Button
            onClick={() => {
              save();
              setSectionIdx((i) => Math.min(SECTIONS.length - 1, i + 1));
            }}
          >
            {t('ieltsMock.next')}
          </Button>
        )}
      </div>

      {/* Выход по аппаратной «Назад» во время экзамена — только с подтверждением:
          идёт отсчёт, и уход стоит попытки. */}
      <Modal open={leaveAsk} onClose={() => setLeaveAsk(false)} title={t('ieltsMock.leaveTitle')} size="sm">
        <p className="text-sm text-on-surface-variant">
          {t('ieltsMock.leaveText')}
        </p>
        <div className="mt-5 flex gap-3">
          <Button variant="secondary" fullWidth onClick={() => setLeaveAsk(false)}>
            {t('ieltsMock.leaveStay')}
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={() => {
              setLeaveAsk(false);
              navigate('/app/trainers');
            }}
          >
            {t('ieltsMock.leaveExit')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
